import React, { useEffect, useState } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useSearch } from '~libs/searchParams';

import { Button, ClickableIcon, ErrorResponse, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import { faMagnifyingGlass } from '~components/icon';
import { TableView } from '~components/table-view-v3';

import { BulkSignupWizard } from '~direct-debits/components/bulk-signup-wizard';
import { columnsFactory } from '~direct-debits/forms/client-signups/list';
import { searchFields } from '~direct-debits/forms/client-signups/search';

import { useGetClientSignupsQuery } from '~direct-debits/actions/clientSignups';
import { setTitle } from '~main/actions/login';

import SignupStatsCards from './SignupStatsCards';

const DEFAULT_LIMIT = 30;
const LOAD_MORE_STEP = 30;

interface ComponentProps {
  isActive?: boolean;
}

const ClientSignupsContainer: React.FC<ComponentProps> = ({ isActive = true }) => {
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [wizardOpen, setWizardOpen] = useState<boolean>(false);
  const [searchParams, setParams] = useSearch('dd-client-signups-search', { limit: DEFAULT_LIMIT });

  const handleSearchSubmit = (values: Record<string, any>) => setParams({ ...values, limit: DEFAULT_LIMIT });

  const { data, isFetching, isError, error } = useGetClientSignupsQuery(searchParams, { skip: !isActive });
  // TableView rows require an `id` field — client_id is the stable key for each row.
  const rows = (data?.rows ?? []).map((row) => ({ ...row, id: row.client_id }));
  const total = data?.total ?? 0;
  const stats = data?.stats;

  useEffect(() => {
    if (isActive) setTitle('Direct Debits — Client Signups');
  }, [isActive]);

  const handleLoadMore = () => {
    const currentLimit = Number(searchParams.limit) || DEFAULT_LIMIT;
    setParams({ ...searchParams, limit: currentLimit + LOAD_MORE_STEP });
  };

  return (
    <div className="p-2 dd-client-signups">
      <SignupStatsCards stats={stats} isLoading={isFetching && !data} />
      <Toolbar
        id="dd-client-signups"
        rightCluster={
          <>
            <Button
              id="direct-debits-bulk-signup-button"
              data-cy="direct-debits-bulk-signup-button"
              color="success"
              size="sm"
              className="me-2"
              onClick={() => setWizardOpen(true)}
            >
              Send Bulk Signup Links
            </Button>
            <ClickableIcon
              className="me-2"
              icon={faMagnifyingGlass}
              id="dd-client-signups-search-button"
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
              ariaLabel="Toggle search bar"
            />
          </>
        }
      />
      <SearchBar
        isOpen={searchBarOpen}
        fields={searchFields}
        initialValues={searchParams}
        onSubmit={handleSearchSubmit}
      />
      {isError ? (
        <ErrorResponse message={getErrorMessage(error)} />
      ) : (
        <TableView
          id="dd-client-signups-table"
          columns={columnsFactory()}
          data={rows}
          isLoading={isFetching}
          emptyMessage="No clients found"
          readonly
          striped
          allowPagination
          totalCount={total}
          onLoadMore={handleLoadMore}
          showCount
          pageSize={Number(searchParams.limit) || DEFAULT_LIMIT}
        />
      )}
      <BulkSignupWizard isOpen={wizardOpen} setIsOpen={setWizardOpen} />
    </div>
  );
};

export default ClientSignupsContainer;
